import type { ApiIdentity } from "@/generated/models/ObjectSerializer";
import type { ApiProfileMin } from "@/generated/models/ApiProfileMin";
import type { ApiDropReaction } from "@/generated/models/ApiDropReaction";
import { getBannerColorValue } from "@/helpers/profile-banner.helpers";

type ReactionEntry = {
  reaction: string;
  profiles: ApiProfileMin[];
  [key: string]: unknown;
};

export const cloneReactionEntries = (
  reactions: readonly ApiDropReaction[] | null | undefined
): ReactionEntry[] => {
  if (!reactions || reactions.length === 0) {
    return [];
  }

  return reactions.map((reaction) => ({
    ...reaction,
    profiles: [...reaction.profiles],
  }));
};

const duplicateProfilesWithoutUser = (
  profiles: ApiProfileMin[],
  userId: string | null
): ApiProfileMin[] => {
  if (!userId) {
    return [...profiles];
  }

  const filteredProfiles: ApiProfileMin[] = [];

  for (const profile of profiles) {
    if (profile.id !== userId) {
      filteredProfiles.push(profile);
    }
  }

  return filteredProfiles;
};

export const removeUserFromReactions = (
  entries: ReactionEntry[],
  userId: string | null
): ReactionEntry[] => {
  const sanitizedEntries: ReactionEntry[] = [];

  for (const entry of entries) {
    const filteredProfiles = duplicateProfilesWithoutUser(
      entry.profiles,
      userId
    );

    if (filteredProfiles.length > 0) {
      sanitizedEntries.push({
        ...entry,
        profiles: filteredProfiles,
      });
    }
  }

  return sanitizedEntries;
};

export const findReactionIndex = (
  entries: ReactionEntry[],
  reactionCode: string
): number => {
  for (let index = 0; index < entries.length; index += 1) {
    if (entries[index]?.reaction === reactionCode) {
      return index;
    }
  }

  return -1;
};

export const toProfileMin = (
  profile: ApiIdentity | null
): ApiProfileMin | null => {
  if (!profile) {
    return null;
  }

  return {
    id: profile.id ?? profile.primary_wallet,
    handle: profile.handle ?? null,
    pfp: profile.pfp ?? null,
    banner1_color: getBannerColorValue(profile.banner1),
    banner2_color: getBannerColorValue(profile.banner2),
    cic: profile.cic,
    rep: profile.rep,
    tdh: profile.tdh,
    tdh_rate: profile.tdh_rate,
    xtdh: profile.xtdh,
    xtdh_rate: profile.xtdh_rate,
    level: profile.level,
    primary_address: profile.primary_wallet,
    subscribed_actions: [],
    archived: false,
    active_main_stage_submission_ids: profile.active_main_stage_submission_ids,
    winner_main_stage_drop_ids: profile.winner_main_stage_drop_ids,
    is_wave_creator: profile.is_wave_creator,
  };
};
